import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User, LoginRequest, RegisterRequest, AuthContextType } from './types';
import { loginUser, registerUser, logoutUser, getCurrentUser } from './api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Проверка авторизации при загрузке приложения
  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const response = await getCurrentUser();
      if (response.success && response.user) {
        setUser(response.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      // Пользователь не авторизован - это нормально
      // Логируем только неожиданные ошибки (не 401)
      if (error instanceof Error && !error.message.includes('401') && !error.message.includes('Not authenticated')) {
        console.error('Unexpected auth check error:', error);
      }
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Авторизация
  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true);
      const response = await loginUser(credentials);
      
      if (response.success && response.user) {
        setUser(response.user);
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Регистрация
  const register = async (credentials: RegisterRequest) => {
    try {
      setIsLoading(true);
      const response = await registerUser(credentials);
      
      if (!response.success) {
        throw new Error(response.message || 'Registration failed');
      }
      
      // После успешной регистрации можно автоматически авторизовать пользователя
      // Но согласно backend API, регистрация не возвращает пользователя, только успех
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Выход из системы
  const logout = async () => {
    try {
      setIsLoading(true);
      await logoutUser();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  };

  // Проверяем авторизацию при монтировании компонента
  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    register,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Хук для использования контекста авторизации
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
